import { useEffect, useRef } from 'react';

import type { NearbyPeer } from '@/api/types';
import { useT } from '@/i18n';
import type { Incoming } from '@/lib/nearbyGuest';

import { Button } from './Button';
import { EmojiReels } from './EmojiReels';
import { closeText, PeerAvatar } from './Nearby';
import styles from './nearby.module.css';
import ui from './ui.module.css';

/** The request on screen when another device offers a bridge: the four emoji to compare, then Accept or Decline. */
export function IncomingDialog({ incoming, me, onAccept, onDecline }: { readonly incoming: Incoming; readonly me: NearbyPeer | null; readonly onAccept: () => void; readonly onDecline: () => void }): React.JSX.Element {
  const t = useT();
  const first = useRef<HTMLButtonElement>(null);
  const { from, phase } = incoming;

  useEffect(() => {
    first.current?.focus();
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onDecline();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
    };
  }, [phase, onDecline]);

  const title = incoming.mode === 'chat' ? t('incoming.chatTitle', { name: from.name }) : t('incoming.secretTitle', { name: from.name });
  const ended = phase === 'closed' || phase === 'failed' || phase === 'mismatch';
  return (
    <div className={ui.backdrop}>
      <div role="alertdialog" aria-modal="true" aria-labelledby="nearby-incoming-title" className={[ui.modal, styles.incoming].join(' ')} data-testid="nearby-incoming">
        <div className={[ui.modalHead, styles.incomingHead].join(' ')}>
          <PeerAvatar peer={from} />
          <span className={styles.peerText}>
            <span id="nearby-incoming-title" className={styles.peerName}>
              {title}
            </span>
            <span className={styles.peerDevice}>{t('incoming.onWifi', { device: from.device })}</span>
          </span>
        </div>
        <div className={ui.modalBody} tabIndex={0}>
          {ended ? (
            <p className={styles.checkText} role="status">
              {phase === 'mismatch' ? t('incoming.mismatch') : phase === 'failed' ? t('incoming.failed') : closeText(incoming.reason, from.name)}
            </p>
          ) : (
            <>
              <EmojiReels emoji={incoming.emoji} testId="nearby-incoming-emoji" />
              <p className={styles.checkText}>{me ? t('incoming.promptNamed', { from: from.name, me: me.name }) : t('incoming.prompt', { from: from.name })}</p>
              {phase === 'receiving' ? (
                <p className={styles.waiting} role="status">
                  <i aria-hidden="true" />
                  {t('incoming.receiving')}
                </p>
              ) : null}
            </>
          )}
        </div>
        <div className={[ui.modalFoot, styles.incomingActions].join(' ')}>
          {ended ? (
            <Button ref={first} variant="secondary" onClick={onDecline}>
              {t('common.close')}
            </Button>
          ) : phase === 'receiving' ? (
            <Button ref={first} variant="secondary" onClick={onDecline}>
              {t('common.cancel')}
            </Button>
          ) : (
            <>
              <Button onClick={onAccept} disabled={incoming.emoji === null} data-testid="nearby-accept">
                {t('common.accept')}
              </Button>
              <Button ref={first} variant="secondary" onClick={onDecline}>
                {t('common.decline')}
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
